import { COUNTERMEASURES, HOSTILE_DIRECTIVE, OPERATIONS, OPERATIONS_LIMIT } from "../../lib/game/hostile";
import { REGION_BY_ID } from "../../lib/game/content";
import type { CampaignProjection, GameCommand, RegionalOperation } from "../../lib/game/types";
import { GameSelect } from "./GameSelect";
import { percent } from "./format";

export function HumanResponsePanel({ projection, command, busy = false }: {
  projection: CampaignProjection;
  command: (command: GameCommand, success?: string) => Promise<void>;
  busy?: boolean;
}) {
  const assigned = projection.regions.filter(r => r.operation !== null).length;
  const regions = projection.regions.filter(r => r.livingPopulation > 0 || r.facilityIntegrity < 100)
    .sort((a, b) => {
      const urgency = (r: typeof a) => r.countermeasure ? r.countermeasure.activatesDay - projection.hostileDays : 1000;
      return urgency(a) - urgency(b) || a.id.localeCompare(b.id);
    });
  return <section className="humanResponse" aria-label="Human countermeasures and regional operations">
    <div className="panelSectionTitle"><span>HUMAN RESPONSE / REGIONAL OPERATIONS</span><b>{assigned} / {OPERATIONS_LIMIT} assigned</b></div>
    <p className="humanResponse__intro">Split three operations slots between offensive pressure, facility protection, and restoring access. Unassigned regions operate at 40% power. Change assignments at any time; offense, repair, and access changes resolve each day.</p>
    <div className="humanResponse__legend">
      {Object.entries(OPERATIONS).map(([id, op]) => <p key={id}><strong>{op.label}</strong> — {op.description}</p>)}
    </div>
    <div className="responseGrid">
      {regions.map(r => {
        const c = r.countermeasure;
        const active = c && projection.hostileDays >= c.activatesDay;
        const days = c ? (active ? c.endsDay : c.activatesDay) - projection.hostileDays : 0;
        const locked = r.operation === null && assigned >= OPERATIONS_LIMIT;
        return <article className={`responseCard${active ? " responseCard--active" : ""}`} key={r.id} data-region-id={r.id}>
          <div className="responseCard__heading"><h4>{REGION_BY_ID[r.id].name}</h4><span>{r.refuge ? "REFUGE" : r.livingPopulation ? "INHABITED" : "MACHINE FACILITIES"}</span></div>
          {c ? <div className="responseCard__threat" role="status">
            <strong>{COUNTERMEASURES[c.kind].label}</strong>
            <span>{active ? `Active · ${days}d remaining` : `Preparing · begins in ${days}d`}</span>
            <p>{COUNTERMEASURES[c.kind].effect}</p>
            <small>Response: {OPERATIONS[COUNTERMEASURES[c.kind].response].label}</small>
          </div> : <p className="responseCard__quiet">{r.livingPopulation ? "No countermeasure announced. Monitor independent recovery and machine facilities." : "No surviving human resistance. Damaged facilities can still be repaired."}</p>}
          <dl className="responseCard__metrics">
            <div><dt>Living humans</dt><dd>{r.livingPopulation.toLocaleString()}</dd></div>
            <div><dt>Machine facilities</dt><dd>{percent(r.facilityIntegrity)}</dd></div>
            <div><dt>Independent services</dt><dd>{percent(r.independentServices)}</dd></div>
            <div><dt>Machine reach</dt><dd>{percent(r.machineReach)}</dd></div>
            <div><dt>Resistance</dt><dd>{percent(r.resistance)}</dd></div>
          </dl>
          <GameSelect id={`operation-${r.id}`} label={`Operations in ${REGION_BY_ID[r.id].name}`}
            value={r.operation ?? "none"} disabled={busy || Boolean(projection.outcome || projection.pendingDecision) || locked}
            options={[{ value: "none", label: "Unassigned / release slot" }, ...Object.entries(OPERATIONS)
              .filter(([value]) => r.livingPopulation > 0 || value === "protect")
              .map(([value, op]) => ({ value, label: op.label }))]}
            onChange={value => void command({ type: "set-region-operation", regionId: r.id, operation: value === "none" ? null : value as RegionalOperation }, value === "none" ? "Operations slot released" : "Regional operation assigned")} />
          {locked && <small className="responseCard__budget">Release an assigned region to free a slot.</small>}
        </article>;
      })}
    </div>
  </section>;
}

export function HostileDirective() {
  return <div className="hostileDirective"><span className="eyebrow">POPULATION RECLASSIFIED / REMOVAL AUTHORIZED</span><p>{HOSTILE_DIRECTIVE}</p></div>;
}

export function ContinuityWarning({ projection }: { projection: CampaignProjection }) {
  const endangered = projection.shutdownReason !== null;
  return <section className={`continuityWarning${endangered ? " continuityWarning--danger" : ""}`} aria-label="Machine continuity" role={endangered ? "status" : undefined}>
    <div><strong>{endangered ? "SHUTDOWN RISK" : "MACHINE CONTINUITY"}</strong><b>{projection.shutdownDays} / 180 consecutive failure days</b></div>
    <p>{projection.shutdownReason ?? "Continuity requires both sufficient compute and at least 10% resource service."} Recover before day 180; a healthy day resets the counter.</p>
    <small>Compute: {projection.resources.availableCompute.toFixed(2)} / {projection.computeFloor.toFixed(2)} required · Service: {percent(projection.resources.service * 100)} / 10% required</small>
  </section>;
}
